import { useSelector } from "react-redux";
import { Navigate } from "react-router-dom";

import { AVAILABLE_PERMISSIONS } from "@/constants/permissions";
import { useAppSelector } from "@/hooks/redux";
import { RootState } from "@/store";

// Map permissions to routes
const permissionRouteMap: Record<string, string> = {
  analytics_view: "/dashboard/analytics",
  application_management: "/dashboard/applications",
  customer_support: "/dashboard/messages",
  Documents_management: "/dashboard/documents",
  FAQs: "/dashboard/faqs",
  financial_data: "/dashboard/finance",
  landing_pages_control: "/dashboard/catch-rides",
  social_links: "/dashboard/social-links",
  upcoming_subscriptions: "/dashboard/subscriptions-management",
  updates_management: "/dashboard/updates",
  user_management: "/dashboard/staff",
  view_audit_trail: "/dashboard/audit-management",
  view_notifications: "/dashboard/notifications-management",
  "zones_&_subzones": "/dashboard/zones-subzones",
};

// Routes accessible to everyone (no permission required)
const universalRoutes = [
  { label: "Settings", path: "/dashboard/settings" },
  { label: "Calendar", path: "/dashboard/calendar" },
];

export default function RootRedirect() {
  const { isAuthenticated, isLoading, user } = useAppSelector(
    (state) => state.auth,
  );

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <span className="loading loading-spinner loading-lg text-primary"></span>
          <p className="mt-4 text-base-content/70">Loading...</p>
        </div>
      </div>
    );
  }

  // If not authenticated, redirect to login
  if (!isAuthenticated || !user) {
    return <Navigate replace to="/auth/login" />;
  }

  // All authorized dashboard roles go to main dashboard
  const DASHBOARD_ROLES = ["admin", "manager", "staff", "employee", "partner"];
  if (!DASHBOARD_ROLES.includes(user.role)) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="w-full max-w-md p-8 text-center">
          <div className="mb-4 text-6xl">🚫</div>
          <h1 className="mb-2 text-2xl font-bold text-error">Access Denied</h1>
          <p className="mb-6 text-base-content/70">
            You are not authorized to access this dashboard. Please contact your
            administrator.
          </p>
          <a className="btn btn-primary" href="/auth/login">
            Back to Login
          </a>
        </div>
      </div>
    );
  }

  // Admin users have all permissions, redirect to home
  if (user.role === "admin") {
    return <Navigate replace to="/dashboard" />;
  }

  // For non-admin users, check if they have view_dashboard permission
  const userPermissions = user.permissions || [];
  
  // If user has view_dashboard permission, redirect to home
  if (userPermissions.includes("view_dashboard")) {
    return <Navigate replace to="/dashboard" />;
  }

  // Check permission-based routes
  for (const permission of userPermissions) {
    const route = permissionRouteMap[permission as keyof typeof permissionRouteMap];
    if (route) {
      return <Navigate replace to={route} />;
    }
  }

  // If no accessible routes found, redirect to calendar (everyone can access it)
  return <Navigate replace to="/dashboard/calendar" />;
}
